import { deleteV2MediaItem } from "../v2Media";
import { deleteSpatialDataset } from "../spatialDatasetService";
import { EngineFile, UploadAdapter } from "./createS3Uppy";
import { postUpload } from "./uploadClient";

/**
 * What differs between the resources that upload through the engine: the
 * routes, where the row comes from, and the file's content type and name.
 */

/**
 * S3 key-safe filename: anything but letters, digits, dots and hyphens becomes
 * an underscore, so CloudFront and MediaConvert never meet an encoded key.
 */
export const sanitizeFilename = (filename: string): string => {
  let sanitized = filename.replace(/[^a-zA-Z0-9._-]/g, "_").replace(/^[_.]+/, "");
  if (!sanitized) sanitized = "upload";
  const lastDot = sanitized.lastIndexOf(".");
  return lastDot > 0 ? sanitized.substring(0, lastDot) + sanitized.substring(lastDot).toLowerCase() : sanitized;
};

const mediaRowBody = (file: EngineFile, uploadMethod: "signed_url" | "s3_multipart") => ({
  name: file.meta.displayName || file.meta.name || file.name,
  description: file.meta.description,
  media_type: file.type,
  // Signed into the PUT URL as its Content-Type, so it must be exactly what the PUT sends
  // (`contentType` below): a browser reports some video containers with no type at all.
  file_type: mediaAdapter.contentType(file),
  file_size: file.size,
  original_file: {
    filename: sanitizeFilename(file.name || "upload"),
    original_filename: file.name,
    type: file.type,
    size: `${Math.round((file.size ?? 0) / 1024)}kb`
  },
  upload_details: { status: "pending", batch_id: file.meta.batch_id, upload_method: uploadMethod },
  exif_metadata: file.meta.exif_metadata ?? null,
  geometry: file.meta.geometry ?? null
});

interface MediaRow {
  id: number;
  upload_details?: { signed_url?: string; file_key?: string };
}

/** Media creates its own row: with the signed PUT for small files, before initialize for multipart. */
export const mediaAdapter: UploadAdapter = {
  resource: "media",
  multipartPath: "v2/media/multipart",
  initialize: (assetId) => ({ path: "v2/media/multipart/initialize/", body: { media_id: assetId } }),
  assetIdField: "media_id",
  contentType: (file) => file.type || "application/octet-stream",
  fileName: (file) => sanitizeFilename(file.name || "upload"),
  createAsset: async (file) => (await postUpload<MediaRow>("v2/media/", mediaRowBody(file, "s3_multipart"))).id,
  deleteAsset: (assetId) => deleteV2MediaItem(assetId),
  signSinglePut: async (file, previousAssetId) => {
    // An earlier attempt's row never got its file; this attempt makes its own.
    if (previousAssetId != null) await deleteV2MediaItem(previousAssetId).catch(() => undefined);
    const row = await postUpload<MediaRow>("v2/media/?return_signed_url=true", mediaRowBody(file, "signed_url"));
    const url = row.upload_details?.signed_url;
    if (!url) {
      await deleteV2MediaItem(row.id).catch(() => undefined);
      throw new Error("No signed URL returned from server");
    }
    return { url, key: row.upload_details?.file_key ?? "", assetId: row.id };
  }
};

/**
 * The caller creates the dataset row and passes it as `meta.assetId`, with the
 * content type for its source format as `meta.contentType`: the browser
 * mislabels GeoJSON, GPX, KML and ZIP. The backend marks the row `queued` on
 * `complete` and deletes it on abort.
 */
export const spatialDatasetAdapter: UploadAdapter = {
  resource: "spatial-dataset",
  multipartPath: "v2/spatial-datasets/multipart",
  initialize: (assetId) => {
    if (assetId == null) throw new Error("A spatial dataset upload needs its dataset id.");
    return { path: `v2/spatial-datasets/${assetId}/multipart/initialize/` };
  },
  assetIdField: "dataset_id",
  contentType: (file) => file.meta.contentType || "application/octet-stream",
  fileName: (file) => file.name || "upload",
  deleteAsset: (assetId) => deleteSpatialDataset(assetId)
};

/** No row exists until the bundle has landed; the caller opens the import from the `s3_key` that `complete` returns. */
export const fieldImportAdapter: UploadAdapter = {
  resource: "field-import",
  multipartPath: "v2/field/imports/multipart",
  initialize: () => ({ path: "v2/field/imports/multipart/initialize/" }),
  // The browser mislabels .zip.
  contentType: () => "application/zip",
  fileName: (file) => file.name || "bundle.zip"
};
